import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Form, Select, Button, DatePicker, Spin, Input, Modal } from 'antd'
import { ProTable, ProColumns } from '@ant-design/pro-components'
import dayjs from 'dayjs'
import { useDebounceFn } from 'ahooks'
import { getBroadcastTaskListApi } from '@/service/broadcast/api/getBroadcastTaskList'
import type { BroadcastTaskItem, BroadcastTaskListData } from '@/service/broadcast/types/getBroadcastTaskList'
import { getChannelListApi } from '@/service/channel/api/getChannelList'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'
import searchIcon from '@/assets/images/search-icon.jpg'
import addIcon from '@/assets/images/add.jpg'
import copyIcon from '@/assets/images/copy.png'
import { copyToClipboard } from '@/utils/clipboard'
import styles from './index.module.scss'
import CreateTask from './component/createTask'
import './component/createTask.module.scss'

const { RangePicker } = DatePicker

const STATUS_OPTIONS = [
    { label: 'Wait', value: 'wait' },
    { label: 'Sent', value: 'sent' },
    { label: 'Sending', value: 'sending' },
]

const STATUS_COLOR_MAP: Record<string, { color: string; bg: string }> = {
    wait: { color: '#FFA94D', bg: 'rgba(255, 169, 77, 0.12)' },
    sent: { color: '#51CF66', bg: 'rgba(81, 207, 102, 0.12)' },
    failed: { color: '#FF4757', bg: 'rgba(255, 71, 87, 0.12)' },
    sending: { color: '#409EFF', bg: 'rgba(64, 158, 255, 0.12)' },
}

const PAGE_SIZE = 10

const COUNT_ITEMS = [
    { key: 'target', label: 'Target' },
    { key: 'delivered', label: 'Delivered' },
    { key: 'sent', label: 'Sent' },
    { key: 'read', label: 'Read' },
] as const

const Broadcast = () => {
    const [form] = Form.useForm()
    const navigate = useNavigate()
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)
    const [loading, setLoading] = useState(false)
    const [list, setList] = useState<BroadcastTaskItem[]>([])
    const [statsData, setStatsData] = useState<BroadcastTaskListData | null>(null)
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const [channelList, setChannelList] = useState<ChannelItemRes[]>([])
    const [createTaskOpen, setCreateTaskOpen] = useState(false)

    useEffect(() => {
        if (!companyName) return
        getChannelListApi({ company: companyName, channel: 'Telegram' }).then(res => {
            if (res.code === 0) {
                setChannelList(res.data.list)
            }
        })
    }, [companyName])


    const fetchList = useCallback((currentPage = 1) => {
        if (!companyName) return
        const values = form.getFieldsValue()
        const [start, end] = values.dateRange || []
        setLoading(true)
        getBroadcastTaskListApi({
            company: companyName,
            channel: 'Telegram',
            channel_id: values.channel_id || '',
            name: values.name || '',
            status: values.status || '',
            start: start ? start.startOf('day').unix() : 0,
            end: end ? end.endOf('day').unix() : 0,
            limit: PAGE_SIZE,
            page: currentPage,
        }).then(res => {
            if (res.code === 0) {
                setList(res.data.list)
                setTotal(res.data.total)
                setStatsData(res.data)
            }
        }).finally(() => setLoading(false))
    }, [companyName, form])

    useEffect(() => {
        fetchList()
    }, [companyName])

    const handleSearch = useCallback(() => {
        setPage(1)
        fetchList(1)
    }, [fetchList])

    const { run: handleAutoSearch } = useDebounceFn(handleSearch, { wait: 500 })

    const handlePageChange = useCallback((p: number) => {
        setPage(p)
        fetchList(p)
    }, [fetchList])

    const deliveryRate = useMemo(() => {
        if (!statsData || !statsData.target) return '0%'
        return ((statsData.delivered / statsData.target) * 100).toFixed(0) + '%'
    }, [statsData])

    const readRate = useMemo(() => {
        if (!statsData || !statsData.delivered) return '0%'
        return ((statsData.read / statsData.delivered) * 100).toFixed(0) + '%'
    }, [statsData])

    const replyRate = useMemo(() => {
        if (!statsData || !statsData.delivered) return '0%'
        return ((statsData.replied / statsData.delivered) * 100).toFixed(0) + '%'
    }, [statsData])

    const columns: ProColumns<BroadcastTaskItem>[] = [
        { title: 'Name', dataIndex: 'name', key: 'name', width: 120 },
        { title: 'Channel Name', dataIndex: 'channel_name', key: 'channel_name', width: 120 },
        {
            title: 'Channel ID',
            dataIndex: 'channel_id',
            key: 'channel_id',
            width: 120,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (_, record) => {
                const status = record.status
                if (!status) return '-'
                const colorCfg = STATUS_COLOR_MAP[status]
                return (
                    <span style={{
                        color: colorCfg?.color || '#717787',
                        background: colorCfg?.bg || 'rgba(113, 119, 135, 0.12)',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: 12,
                    }}>
                        {status}
                    </span>
                )
            },
        },
        { title: 'Target', dataIndex: 'target', key: 'target', width: 80 },
        { title: 'Sent', dataIndex: 'sent', key: 'sent', width: 80 },
        { title: 'Delivered', dataIndex: 'delivered', key: 'delivered', width: 100 },
        { title: 'Read', dataIndex: 'read', key: 'read', width: 80 },
        { title: 'Replied', dataIndex: 'replied', key: 'replied', width: 80 },
        { title: 'Failed', dataIndex: 'failed', key: 'failed', width: 80 },
        {
            title: 'Schedule Time',
            dataIndex: 'schedule_time',
            key: 'schedule_time',
            width: 180,
            render: (_, record) =>
                record.schedule_time ? dayjs.unix(record.schedule_time).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: 'Send Time',
            dataIndex: 'send_time',
            key: 'send_time',
            width: 180,
            render: (_, record) =>
                record.send_time ? dayjs.unix(record.send_time).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: 'Create Time',
            dataIndex: 'create_time',
            key: 'create_time',
            width: 180,
            render: (_, record) =>
                record.create_time ? dayjs(record.create_time).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
    ]

    return (
        <div className={styles.broadcastPage}>
            {/* 搜索区域 */}
            <div className={styles.searchWrapper}>
                <Form
                    form={form}
                    layout="inline"
                    className={styles.searchForm}
                    initialValues={{
                        dateRange: [dayjs().subtract(7, 'day'), dayjs()],
                    }}
                    onValuesChange={handleAutoSearch}
                >
                    <Form.Item name="dateRange">
                        <RangePicker
                            format="YYYY-MM-DD"
                            allowClear={false}
                            className={styles.dateRangePicker}
                            popupClassName={styles.dateRangePopup}
                            disabledDate={(current) => {
                                return current && (
                                    current < dayjs().subtract(2, 'month').startOf('day')
                                );
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="channel_id">
                        <Select
                            placeholder="Channel ID"
                            allowClear
                            className={styles.searchInput}
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            options={channelList.map(item => ({
                                label: `${item.channel_name} (${item.channel_id})`,
                                value: item.channel_id,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="name">
                        <Input
                            prefix={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                            placeholder="Task Name"
                            allowClear
                            autoComplete="off"
                            className={styles.searchInput}
                        />
                    </Form.Item>
                    <Form.Item name="status">
                        <Select
                            options={STATUS_OPTIONS}
                            placeholder="Status"
                            allowClear
                            className={styles.searchInput}
                        />
                    </Form.Item>
                </Form>
                <div className={styles.buttonGroup}>
                    <Button
                        type="primary"
                        icon={<img src={searchIcon} style={{ width: 14, height: 14 }} />}
                        style={{ gap: '2px' }}
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                    <Button
                        type="primary"
                        style={{ width: '100px' }}
                        onClick={() => setCreateTaskOpen(true)}
                    >
                        <img src={addIcon} style={{ width: 14, height: 14 }} />
                        <span>Create Task</span>
                    </Button>
                </div>
            </div>

            {/* 内容区域：左侧统计 + 右侧表格 */}
            <Spin spinning={loading} wrapperClassName={styles.spinWrapper}>
                <div className={styles.contentWrapper}>
                    {/* 左侧统计卡片 */}
                    <div className={styles.statsPanel}>
                        <div className={styles.cardTitle}>
                            <span className={styles.titleDot} />
                            Task Statistics
                        </div>
                        <div className={styles.miniGrid}>
                            {COUNT_ITEMS.map(item => (
                                <div key={item.key} className={styles.miniCard}>
                                    <span className={styles.miniCardLabel}>{item.label}</span>
                                    <span className={styles.miniCardValue}>
                                        {statsData?.[item.key] ?? 0}
                                    </span>
                                </div>
                            ))}
                            <div className={styles.miniCard}>
                                <span className={styles.miniCardLabel}>Replies</span>
                                <span className={styles.miniCardValue}>{statsData?.replied ?? 0}</span>
                            </div>
                            <div className={styles.miniCard}>
                                <span className={styles.miniCardLabel}>Delivered Rate</span>
                                <span className={styles.miniCardValue}>{deliveryRate}</span>
                            </div>
                            <div className={styles.miniCard}>
                                <span className={styles.miniCardLabel}>Read Rate</span>
                                <span className={styles.miniCardValue}>{readRate}</span>
                            </div>
                            <div className={styles.miniCard}>
                                <span className={styles.miniCardLabel}>Reply Rate</span>
                                <span className={styles.miniCardValue}>{replyRate}</span>
                            </div>
                        </div>
                        <div className={styles.totalRow}>
                            <span>Total Tasks</span>
                            <span className={styles.totalValue}>{total}</span>
                        </div>
                    </div>

                    {/* 右侧表格 */}
                    <div className={styles.tableWrapper}>
                        <ProTable<BroadcastTaskItem>
                            className="broadcast-full-table"
                            rowKey="id"
                            columns={columns}
                            dataSource={list}
                            search={false}
                            options={false}
                            toolBarRender={false}
                            tableAlertRender={false}
                            onRow={(record) => ({
                                onClick: () => {
                                    if (record.status === 'sending') return
                                    navigate(`/broadcast/detail/${record.id}`, {
                                        state: {
                                            channel_name: record.channel_name,
                                            channel_id: record.channel_id,
                                            name: record.name,
                                        },
                                    })
                                },
                                style: { cursor: record.status === 'sending' ? 'not-allowed' : 'pointer' },
                            })}
                            pagination={{
                                current: page,
                                pageSize: PAGE_SIZE,
                                total,
                                showSizeChanger: false,
                                onChange: handlePageChange,
                            }}
                        />
                    </div>
                </div>
            </Spin>
            <Modal
                title="Create Task"
                open={createTaskOpen}
                onCancel={() => setCreateTaskOpen(false)}
                footer={null}
                destroyOnClose
                width={700}
                centered
                className="create-task-modal"
            >
                <CreateTask
                    onSuccess={() => {
                        setCreateTaskOpen(false)
                        fetchList(1)
                    }}
                    onCancel={() => setCreateTaskOpen(false)}
                />
            </Modal>
        </div>
    )
}

export default Broadcast